import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { providersService, configService, aiService } from '../../services';

// Async thunks for API calls
export const fetchAIProviders = createAsyncThunk(
  'providers/fetchAIProviders',
  async (_, { rejectWithValue }) => {
    try {
      const data = await aiService.models();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchVoiceProviders = createAsyncThunk(
  'providers/fetchVoiceProviders',
  async (_, { rejectWithValue }) => {
    try {
      const data = await providersService.list();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCurrentConfig = createAsyncThunk(
  'providers/fetchCurrentConfig',
  async (_, { rejectWithValue }) => {
    try {
      const data = await configService.get();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateConfig = createAsyncThunk(
  'providers/updateConfig',
  async (configData, { rejectWithValue }) => {
    try {
      const data = await configService.update(configData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOpenRouterModels = createAsyncThunk(
  'providers/fetchOpenRouterModels',
  async (_, { rejectWithValue }) => {
    try {
      // Using a service method would be better, but for external APIs we can keep direct fetch
      const response = await fetch('https://openrouter.ai/api/v1/models');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data.map(model => ({
        value: model.id,
        label: model.id,
      }));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // AI Providers
  aiProviders: [],
  aiProvidersLoading: false,
  aiProvidersError: null,

  // Voice Providers
  voiceProviders: [],
  voiceProvidersLoading: false,
  voiceProvidersError: null,

  // Current Configuration
  currentConfig: {},
  configLoading: false,
  configError: null,

  // OpenRouter Models
  openRouterModels: [],
  openRouterModelsLoading: false,
  openRouterModelsError: null,

  // Provider Options (hardcoded)
  providerOptions: {
    brain: [
      { value: 'openrouter', label: 'OpenRouter', requiresKey: true },
      { value: 'openai', label: 'OpenAI', requiresKey: true },
      { value: 'anthropic', label: 'Anthropic', requiresKey: true },
      { value: 'google', label: 'Google Gemini', requiresKey: true },
      { value: 'perplexity', label: 'Perplexity', requiresKey: true },
      { value: 'self-hosted', label: 'Self-hosted', requiresKey: false },
    ],
    tts: [
      { value: 'indicf5_hindi', label: 'IndicF5 (Hindi)', requiresKey: false },
      { value: 'deepgram', label: 'Deepgram Aura', requiresKey: true },
      { value: 'elevenlabs', label: 'ElevenLabs', requiresKey: true },
      { value: 'google', label: 'Google TTS', requiresKey: true },
      { value: 'piper', label: 'Piper TTS', requiresKey: false },
      { value: 'melotts', label: 'MeloTTS', requiresKey: false },
      { value: 'self-hosted', label: 'Self-hosted', requiresKey: false },
    ],
    stt: [
      { value: 'openai-whisper', label: 'OpenAI Whisper', requiresKey: true },
      { value: 'vosk', label: 'Vosk', requiresKey: false },
      { value: 'google', label: 'Google STT', requiresKey: true },
      { value: 'self-hosted', label: 'Self-hosted', requiresKey: false },
    ],
  },

  // Model Options (hardcoded)
  modelOptions: {
    brain: {
      openai: [
        { value: 'gpt-4', label: 'GPT-4' },
        { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
      ],
      anthropic: [
        { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
        { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
      ],
      google: [{ value: 'gemini-pro', label: 'Gemini Pro' }],
      perplexity: [
        { value: 'llama-3-8b-instruct', label: 'Llama 3 8B Instruct' },
        { value: 'llama-3-70b-instruct', label: 'Llama 3 70B Instruct' },
      ],
      'self-hosted': [],
    },
    tts: {
      elevenlabs: [
        { value: 'rachel', label: 'Rachel' },
        { value: 'domi', label: 'Domi' },
      ],
      indicf5_hindi: [{ value: 'default', label: 'Default (Hindi)' }],
      deepgram: [
        { value: 'aura-asteria-en', label: 'Asteria (Female)' },
        { value: 'aura-luna-en', label: 'Luna (Female)' },
        { value: 'aura-stella-en', label: 'Stella (Female)' },
        { value: 'aura-athena-en', label: 'Athena (Female)' },
        { value: 'aura-hera-en', label: 'Hera (Female)' },
        { value: 'aura-orion-en', label: 'Orion (Male)' },
        { value: 'aura-arcas-en', label: 'Arcas (Male)' },
        { value: 'aura-perseus-en', label: 'Perseus (Male)' },
        { value: 'aura-angus-en', label: 'Angus (Male)' },
        { value: 'aura-orpheus-en', label: 'Orpheus (Male)' },
        { value: 'aura-helios-en', label: 'Helios (Male)' },
        { value: 'aura-zeus-en', label: 'Zeus (Male)' },
      ],
      google: [{ value: 'en-US-Wavenet-A', label: 'Wavenet A' }],
      piper: [{ value: 'en_US-amy-medium', label: 'Amy (US)' }],
      melotts: [{ value: 'en-US-Standard', label: 'Standard (US)' }],
      'self-hosted': [],
    },
    stt: {
      'openai-whisper': [{ value: 'whisper-1', label: 'Whisper-1' }],
      vosk: [{ value: 'vosk-model-small-en-us', label: 'Vosk Small English' }],
      google: [{ value: 'default', label: 'Default' }],
      'self-hosted': [],
    },
  },
};

const providersSlice = createSlice({
  name: 'providers',
  initialState,
  reducers: {
    clearError: state => {
      state.aiProvidersError = null;
      state.voiceProvidersError = null;
      state.configError = null;
      state.openRouterModelsError = null;
    },
    setCurrentConfig: (state, action) => {
      state.currentConfig = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch AI Providers
      .addCase(fetchAIProviders.pending, state => {
        state.aiProvidersLoading = true;
        state.aiProvidersError = null;
      })
      .addCase(fetchAIProviders.fulfilled, (state, action) => {
        state.aiProvidersLoading = false;
        state.aiProviders = action.payload;
      })
      .addCase(fetchAIProviders.rejected, (state, action) => {
        state.aiProvidersLoading = false;
        state.aiProvidersError = action.payload;
      })
      // Fetch Voice Providers
      .addCase(fetchVoiceProviders.pending, state => {
        state.voiceProvidersLoading = true;
        state.voiceProvidersError = null;
      })
      .addCase(fetchVoiceProviders.fulfilled, (state, action) => {
        state.voiceProvidersLoading = false;
        state.voiceProviders = action.payload;
      })
      .addCase(fetchVoiceProviders.rejected, (state, action) => {
        state.voiceProvidersLoading = false;
        state.voiceProvidersError = action.payload;
      })
      // Fetch Current Config
      .addCase(fetchCurrentConfig.pending, state => {
        state.configLoading = true;
        state.configError = null;
      })
      .addCase(fetchCurrentConfig.fulfilled, (state, action) => {
        state.configLoading = false;
        state.currentConfig = action.payload;
      })
      .addCase(fetchCurrentConfig.rejected, (state, action) => {
        state.configLoading = false;
        state.configError = action.payload;
      })
      // Update Config
      .addCase(updateConfig.pending, state => {
        state.configLoading = true;
        state.configError = null;
      })
      .addCase(updateConfig.fulfilled, (state, action) => {
        state.configLoading = false;
        state.currentConfig = action.payload;
      })
      .addCase(updateConfig.rejected, (state, action) => {
        state.configLoading = false;
        state.configError = action.payload;
      })
      // Fetch OpenRouter Models
      .addCase(fetchOpenRouterModels.pending, state => {
        state.openRouterModelsLoading = true;
        state.openRouterModelsError = null;
      })
      .addCase(fetchOpenRouterModels.fulfilled, (state, action) => {
        state.openRouterModelsLoading = false;
        state.openRouterModels = action.payload;
      })
      .addCase(fetchOpenRouterModels.rejected, (state, action) => {
        state.openRouterModelsLoading = false;
        state.openRouterModelsError = action.payload;
      });
  },
});

export const { clearError, setCurrentConfig } = providersSlice.actions;

// Selectors
export const selectAIProviders = state => state.providers.aiProviders;
export const selectVoiceProviders = state => state.providers.voiceProviders;
export const selectCurrentConfig = state => state.providers.currentConfig;
export const selectOpenRouterModels = state => state.providers.openRouterModels;
export const selectProviderOptions = state => state.providers.providerOptions;
export const selectModelOptions = state => state.providers.modelOptions;

// Loading selectors
export const selectAIProvidersLoading = state => state.providers.aiProvidersLoading;
export const selectVoiceProvidersLoading = state => state.providers.voiceProvidersLoading;
export const selectConfigLoading = state => state.providers.configLoading;
export const selectOpenRouterModelsLoading = state => state.providers.openRouterModelsLoading;

// Error selectors
export const selectAIProvidersError = state => state.providers.aiProvidersError;
export const selectVoiceProvidersError = state => state.providers.voiceProvidersError;
export const selectConfigError = state => state.providers.configError;
export const selectOpenRouterModelsError = state => state.providers.openRouterModelsError;

export default providersSlice.reducer;
